<template>
  <ModalDialog
    contentClass="termination-modal-content"
    :canBeClosed="false"
    open
  >
    <template v-slot:header>
      <h3>Product Termination</h3>
    </template>
    <template v-slot:content>
      <p data-cy="termination-modal-message">
        Adding products to cart for termination. Please wait
      </p>
      <Loader size="large" data-cy="termination-modal-loader" />
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import type { IdAndName } from "@/Domain/IdAndName";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { onMounted } from "vue";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { Loader } from "@progress/kendo-vue-indicators";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useScheduleBStore } from "@/stores/useScheduleBStore";

const main = useMainStore();
const scheduleStore = useScheduleBStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  plants: IdAndName[];
  productIds: number[];
}>();

const emit = defineEmits<{
  (e: "closeModal", cartCount: number): void;
}>();

const closeModal = function (cartCount: number = -1) {
  if (cartCount === -1) {
    notificationStore.notify({
      type: NotificationTypeEnum.error,
      message: "Error adding products to cart for termination",
      duration: 5000,
    });
    emit("closeModal", cartCount);
  } else {
    notificationStore.notify({
      type: NotificationTypeEnum.success,
      message: `Product(s) added to the cart for termination. ${cartCount} product(s) in the cart`,
      duration: 5000,
    });
    scheduleStore.setCartCount(SubApplicationType.Terminate, cartCount);
  }

  emit("closeModal", cartCount);
};

const addToTerminateCartAsync = async function () {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  return handleResponsePromiseAsync(
    api.get("VueAddProductToCart", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication: SubApplicationType.Terminate.toString(),
      prId: props.productIds.join(";"),
      terminationDate: currentDate.toLocaleString("en-US"),
      ownsId: "",
      plantName: "",
    }),
    (data) => {
      closeModal(data);
    },
    "TerminationModal.vue",
    "addToTerminateCart",
    (error) => {
      console.log(error);
      closeModal();
    }
  );
};

onMounted(async () => {
  await addToTerminateCartAsync();
});
</script>

<style lang="scss">
.termination-modal-content {
  max-width: 600px;

  p {
    text-align: center;
    font-weight: bold;
    margin: 1.5rem;
  }

  .k-loader {
    display: block;
    margin: 0 auto;

    &-canvas {
      margin: 0 auto;
    }
  }
}
</style>
